import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Logo from "@/assets/img/eventLogo1.svg";
import {
  CalendarClock,
  Menu,
  X,
  Users,
  PlayCircle,
  Calendar,
  LogOut,
  User,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { getCurrentClientData } from "@/services/authServices";

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const loggedInUser = getCurrentClientData();

  useEffect(() => {
    // Check if user is authenticated
    if (loggedInUser.token) {
      setIsAuthenticated(true);
    }
  }, [loggedInUser]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleSignOut = () => {
    // Clear authentication data
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.clear();
    sessionStorage.clear();

    setIsAuthenticated(false);

    toast({
      title: "Signed out",
      description: "You have been successfully signed out.",
    });

    navigate("/");
  };

  return (
    <nav className="bg-white border-b shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img
                src={Logo}
                alt="CecureStreams Logo"
                className="h-full w-[200px] text-cecure-700"
              />
              {/* <CalendarClock className="h-8 w-8 text-cecure-700" />
              <span className="ml-2 text-xl font-bold text-cecure-900 font-heading">
                CecureStreams <span className="text-accent">Events</span>
              </span> */}
            </Link>
            <div className="hidden md:ml-6 md:flex md:space-x-8">
              <Link
                to="/events"
                className="inline-flex items-center px-1 pt-1 text-sm font-medium text-cecure-700 hover:text-cecure-900 hover:border-b-2 hover:border-cecure-500"
              >
                <Calendar className="mr-1 h-4 w-4" />
                Events
              </Link>
              {isAuthenticated && (
                <>
                  <Link
                    to="/create"
                    className="inline-flex items-center px-1 pt-1 text-sm font-medium text-cecure-700 hover:text-cecure-900 hover:border-b-2 hover:border-cecure-500"
                  >
                    <PlayCircle className="mr-1 h-4 w-4" />
                    Create Event
                  </Link>
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center px-1 pt-1 text-sm font-medium text-cecure-700 hover:text-cecure-900 hover:border-b-2 hover:border-cecure-500"
                  >
                    <Users className="mr-1 h-4 w-4" />
                    Dashboard
                  </Link>
                </>
              )}
            </div>
          </div>
          <div className="hidden md:flex items-center">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="rounded-full h-10 w-10 p-0"
                  >
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => navigate("/dashboard")}>
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/profile")}>
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={handleSignOut}
                    className="text-red-600"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/signin">
                  <Button variant="outline" className="mr-3">
                    Sign In
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button className="bg-cecure-700 hover:bg-cecure-800">
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-cecure-700 hover:text-cecure-900 hover:bg-gray-100 focus:outline-none"
            >
              {mobileMenuOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden">
          <div className="pt-2 pb-3 space-y-1">
            <Link
              to="/events"
              className="block pl-3 pr-4 py-2 text-base font-medium text-cecure-700 hover:text-cecure-900 hover:bg-gray-50"
            >
              Events
            </Link>
            {isAuthenticated && (
              <>
                <Link
                  to="/create"
                  className="block pl-3 pr-4 py-2 text-base font-medium text-cecure-700 hover:text-cecure-900 hover:bg-gray-50"
                >
                  Create Event
                </Link>
                <Link
                  to="/dashboard"
                  className="block pl-3 pr-4 py-2 text-base font-medium text-cecure-700 hover:text-cecure-900 hover:bg-gray-50"
                >
                  Dashboard
                </Link>
              </>
            )}
            <div className="pt-4 pb-3 border-t border-gray-200">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/profile"
                    className="block pl-3 pr-4 py-2 text-base font-medium text-cecure-700 hover:text-cecure-900 hover:bg-gray-50"
                  >
                    Profile
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="block w-full text-left pl-3 pr-4 py-2 text-base font-medium text-red-600 hover:text-red-800 hover:bg-gray-50"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <div className="flex items-center px-4 space-x-3">
                  <Link to="/signin" className="w-full">
                    <Button variant="outline" className="w-full justify-center">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/signup" className="w-full">
                    <Button className="w-full justify-center bg-cecure-700 hover:bg-cecure-800">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
